// Frontend wrappers for schedule (on-call rotation) Cloud Function
// callables, plus a copy of the pure on-call resolver so timeline /
// team-detail UI can compute current on-call without a round trip.

import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import type { Schedule } from './types';

/**
 * Outcome of resolving "who is on call now" against a schedule. Mirrors
 * the OnCallResolution type the server-side resolver returns, but
 * lives here so the UI doesn't depend on functions code.
 */
export interface OnCallResolution {
  userId: string;
  displayName: string;
  source: 'rotation' | 'override';
  rotationIdx?: number;
  endsAtMs: number;
}

/**
 * Returns every override on the schedule, honouring both the modern
 * `overrides[]` array and the legacy singular `currentOverride`
 * field. Use this whenever reading overrides — never touch the raw
 * fields directly from UI code.
 */
export function effectiveOverrides(
  schedule: Pick<Schedule, 'overrides' | 'currentOverride'>,
): NonNullable<Schedule['overrides']> {
  if (schedule.overrides && schedule.overrides.length > 0) return schedule.overrides;
  if (schedule.currentOverride) {
    // Legacy single-override docs predate the array migration. They
    // may not carry an `id` field either — synthesise a stable one
    // so UI keying still works until the next write migrates the doc.
    const legacy = schedule.currentOverride;
    return [{ ...legacy, id: legacy.id ?? 'legacy' }];
  }
  return [];
}

/**
 * Pure on-call math, kept in sync with the server-side resolver
 * (functions/src/schedules/_resolver.ts). Same inputs, same output —
 * UI and the escalation engine MUST agree on this, so any change has
 * to land in both copies. Tests on each side pin the behaviour.
 */
export function resolveOnCallUser(
  schedule: Pick<
    Schedule,
    'userIds' | 'shiftLengthHours' | 'startAt' | 'overrides' | 'currentOverride' | 'userDisplayNames'
  >,
  nowMs: number,
): OnCallResolution | null {
  if (!schedule.userIds || schedule.userIds.length === 0) return null;

  // Any override whose window contains `nowMs` wins. Overrides don't
  // overlap (enforced at write time) so at most one matches. Future-
  // scheduled overrides (fromMs > now) don't apply yet — fall through
  // to the rotation. Matches the server resolver behaviour.
  const overrides = effectiveOverrides(schedule);
  const active = overrides.find(
    (ov) => ov.fromMs <= nowMs && ov.untilMs > nowMs,
  );
  if (active) {
    return {
      userId: active.userId,
      displayName: active.userDisplayName ?? active.userId,
      source: 'override',
      endsAtMs: active.untilMs,
    };
  }

  const shiftMs = schedule.shiftLengthHours * 60 * 60 * 1000;
  if (shiftMs <= 0) return null;
  const elapsed = nowMs - schedule.startAt;
  let slotIdx: number;
  let slotStartMs: number;
  if (elapsed < 0) {
    slotIdx = 0;
    slotStartMs = schedule.startAt;
  } else {
    const wholeShifts = Math.floor(elapsed / shiftMs);
    slotIdx = wholeShifts % schedule.userIds.length;
    slotStartMs = schedule.startAt + wholeShifts * shiftMs;
  }
  const userId = schedule.userIds[slotIdx];
  return {
    userId,
    displayName: schedule.userDisplayNames?.[userId] ?? userId,
    source: 'rotation',
    rotationIdx: slotIdx,
    endsAtMs: slotStartMs + shiftMs,
  };
}

export interface RotationSlot {
  startMs: number;
  endMs: number;
  userId: string;
  displayName: string;
  rotationIdx: number;
}

/**
 * Materialises shifts in [fromMs, toMs] so the timeline UI can render
 * bars without per-pixel math. Kept in sync with the server-side
 * helper of the same name. The renderer is free to overlay override
 * windows on top of these rotation slots.
 */
export function rotationSlotsInRange(
  schedule: Pick<
    Schedule,
    'userIds' | 'shiftLengthHours' | 'startAt' | 'userDisplayNames'
  >,
  fromMs: number,
  toMs: number,
): RotationSlot[] {
  if (!schedule.userIds || schedule.userIds.length === 0) return [];
  if (fromMs >= toMs) return [];
  const shiftMs = schedule.shiftLengthHours * 60 * 60 * 1000;
  if (shiftMs <= 0) return [];

  const out: RotationSlot[] = [];
  let cursor: number;
  let slotIdx: number;
  if (fromMs < schedule.startAt) {
    cursor = schedule.startAt;
    slotIdx = 0;
  } else {
    const wholeShifts = Math.floor((fromMs - schedule.startAt) / shiftMs);
    cursor = schedule.startAt + wholeShifts * shiftMs;
    slotIdx = wholeShifts % schedule.userIds.length;
  }
  while (cursor < toMs) {
    const slotEnd = cursor + shiftMs;
    const userId = schedule.userIds[slotIdx];
    out.push({
      startMs: Math.max(cursor, fromMs),
      endMs: Math.min(slotEnd, toMs),
      userId,
      displayName: schedule.userDisplayNames?.[userId] ?? userId,
      rotationIdx: slotIdx,
    });
    cursor = slotEnd;
    slotIdx = (slotIdx + 1) % schedule.userIds.length;
  }
  return out;
}

// ---------- Callables ----------

export async function createSchedule(
  orgId: string,
  input: {
    /** Optional — schedules can be org-level for MVP. */
    teamId?: string;
    name: string;
    userIds: string[];
    shiftLengthHours: number;
    startAt: number;
    timezone?: string;
  },
): Promise<{ scheduleId: string }> {
  const callable = httpsCallable<typeof input & { orgId: string }, { scheduleId: string }>(
    functions,
    'createSchedule',
  );
  const result = await callable({ orgId, ...input });
  return result.data;
}

export async function updateSchedule(
  orgId: string,
  scheduleId: string,
  patch: {
    name?: string;
    userIds?: string[];
    shiftLengthHours?: number;
    startAt?: number;
    teamId?: string;
  },
): Promise<void> {
  const callable = httpsCallable(functions, 'updateSchedule');
  await callable({ orgId, scheduleId, ...patch });
}

export async function deleteSchedule(
  orgId: string,
  scheduleId: string,
): Promise<void> {
  const callable = httpsCallable(functions, 'deleteSchedule');
  await callable({ orgId, scheduleId });
}

export async function setScheduleOverride(
  orgId: string,
  scheduleId: string,
  input: { userId: string; fromMs?: number; untilMs: number; reason?: string },
): Promise<{ overrideId: string }> {
  const callable = httpsCallable<
    typeof input & { orgId: string; scheduleId: string },
    { overrideId: string }
  >(functions, 'setScheduleOverride');
  const result = await callable({ orgId, scheduleId, ...input });
  return result.data;
}

/**
 * Remove an override by its id. Omit `overrideId` to clear ALL
 * overrides on the schedule — useful as a panic-clear button, kept
 * for back-compat with the singular-override era.
 */
export async function clearScheduleOverride(
  orgId: string,
  scheduleId: string,
  overrideId?: string,
): Promise<void> {
  const callable = httpsCallable(functions, 'clearScheduleOverride');
  await callable({ orgId, scheduleId, overrideId });
}

/**
 * Common shift-length presets surfaced in UI dropdowns. The schedule
 * doc stores an arbitrary number so admins aren't locked into these,
 * but most rotations land in one of these four buckets.
 */
export const SHIFT_LENGTH_PRESETS: ReadonlyArray<{ label: string; hours: number }> = [
  { label: '8 hours', hours: 8 },
  { label: '12 hours', hours: 12 },
  { label: 'Daily (24 hours)', hours: 24 },
  { label: 'Weekly (7 days)', hours: 168 },
  { label: 'Fortnightly (14 days)', hours: 336 },
];

/**
 * Stable colour per user for timeline bars. Hashes the userId to one
 * of a curated palette tuned to the dark theme. Two users on the same
 * page will reliably get distinct, readable colours.
 */
const TIMELINE_PALETTE = [
  '#7c5cff', // accent (matches brand)
  '#3da9fc',
  '#2dd4bf',
  '#f5a524',
  '#f97316',
  '#ec4899',
  '#a78bfa',
  '#22c55e',
  '#06b6d4',
  '#eab308',
] as const;

export function colourForUser(userId: string): string {
  let hash = 0;
  for (let i = 0; i < userId.length; i += 1) {
    hash = (hash * 31 + userId.charCodeAt(i)) | 0;
  }
  return TIMELINE_PALETTE[Math.abs(hash) % TIMELINE_PALETTE.length];
}
